<template>
  <td
    class="report-cell"
    :class="{header: this.header}"
  >
    <img
      v-if="isImage"
      :src="formattedContent"
    >
    <a
      v-else-if="isURL"
      :href="textAfterSeperator"
      class="data-url"
      target="_blank"
    >
      {{ abbreviated ? `${formattedContent.substring(0, 20)}...` : formattedContent }}
    </a>
    <div
      v-else-if="formattedContent !== undefined"
      @click="cycleFormats"
    >
      {{ formattedContent }}
    </div>
    <div
      v-else
    >
      -
    </div>
  </td>
</template>

<script>
import moment from 'moment'

export default {
  name: 'OwalReportCell',
  props: {
    /** @prop {Array | String | Object | Number} content The raw data that is formatted and then displayed in the cell. */
    content: {
      type: [Array, String, Object, Number],
    },
    /** @prop {Boolean} header Determines if the cell is a header cell. */
    header: {
      type: Boolean,
      default: false,
    },
    /** @prop {Boolean} abbreviated Determines if the URL of a cell is abbreviated. */
    abbreviated: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      separator: '|',
      formats: ['MM/DD/YYYY hh:mm A', 'ddd, MM/DD/YYYY hh:mm:ss A'],
      formatIndex: 0,
    }
  },
  computed: {
    /** Computes the display text based on the content. */
    formattedContent () {
      if (this.content === null || this.content === undefined) {
        return undefined
      } else if (this.isURL) {
        return this.textBeforeSeperator
      } else if (this.isDate) {
        const formatted = moment.utc(this.content).local().format('MM/DD/YYYY hh:mm A')
        // some unit strings are being returned as invalide, e.g. '18C', and strict parsing misses other valid dates
        // https://github.com/moment/moment/issues/4702 and https://stackoverflow.com/questions/19978953/moment-js-isvalid-function-not-working-properly
        return formatted !== 'Invalid date' ? formatted : this.content
      } else if (this.isString) {
        return this.content
      } else if (this.isArray) {
        return this.content.join(', ')
      }
      // Fallback, assume plaintext.
      return this.content
    },
    /** --- Methods for detecting content type. --- */
    /** @returns {Boolean} `true` if the data is a string. */
    isString () {
      return typeof this.content === 'string'
    },
    /** @returns {Boolean} `true` if the data is a string and contains a prefix type hint. */
    containsSeperator () {
      return this.indexOfSeperator !== -1
    },
    /** @returns {Number} The first occurence of the format seperator in the string. */
    indexOfSeperator () {
      return this.isString ? this.content.indexOf(this.separator) : -1
    },
    /** @returns {string} A hint for how the cell should format the contents. */
    textBeforeSeperator () {
      if (this.containsSeperator) {
        return this.content.substr(0, this.indexOfSeperator)
      }
      return this.content
    },
    /** @returns {string} A string that does not include the formatting hint prefix. */
    textAfterSeperator () {
      if (this.containsSeperator) {
        const sub = this.content.substr(this.indexOfSeperator + 1)
        return sub
      } else {
        return this.content
      }
    },
    /** @returns {Boolean} `true` if the data is a URL. */
    isURL () {
      if (!this.isString) {
        return false
      }
      if (!this.containsSeperator) {
        return this.content.startsWith('http')
      }
      return this.textAfterSeperator.startsWith('http')
    },
    /** @returns {Boolean} `true` if the data is a URL to an image. */
    isImage () {
      const data = this.content
      return this.isURL && ['.jpg', '.jpeg', '.png', 'gif'].some(format => data.endsWith(format))
    },
    /** @return {Boolean} `true` if a number, else `false`. */
    isNumber () {
      return !isNaN(this.content)
    },
    /** @returns {Boolean} `true` if the data is a Date, otherwise `false`. */
    isDate () {
      const momentObj = moment(this.content, ['YYYY-MM-DDTHH:mm:ss', moment.ISO_8601], true)

      if (this.isNumber || this.isUnit) {
        return false
      }
      return momentObj.isValid()
    },
    isUnit () {
      return this.isString && (this.content.match(/^\w{3}\d{1,2}\w$/i) !== null)
    },
    isArray () {
      return Array.isArray(this.content)
    },
  },
  methods: {
    /** --- Methods for converting the data into the correct display format. --- */
    /**
     * Converts an array into a string.
     *
     * @param {array<String>} array An array of strings to format.
     * @returns A string representation of the date.
     * @method formattedArray
     */
    formattedArray (array) {
      return array.join(', ')
    },
    /**
     * Converts a date into a string.
     *
     * @param {date} date The date object to format.
     * @returns A string representation of the date.
     * @method formattedDate
     */
    formattedDate (date) {
      const format = this.formats[this.formatIndex]
      return moment(date).format(format)
    },
    /**
     * Cycle between date formats.
     */
    cycleFormats () {
      if (!this.isDate) {
        return
      }
      this.formatIndex = this.formatIndex === 1 ? 0 : 1
    },
  },
}
</script>

<style scoped>
.report-cell {
  display: flex;
  flex-grow: 1;
  text-align: center;
  padding: 0;
  margin: 0;
  justify-content: center;
  align-items: center;
  align-content: center;
}

.report-cell img {
  max-width: 100%;
}
</style>
